import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  NavLink,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import {
  Users,
  AlertCircle,
  GraduationCap,
  LogOut,
} from "lucide-react";
import Classes from "./pages/Classes";
import ClassDetail from "./pages/ClassDetail";
import Students from "./pages/Students";
import StudentDetail from "./pages/StudentDetail";
import FollowUps from "./pages/FollowUps";
import Homework from "./pages/Homework";
import Login from "./pages/Login";
import api from "./api/client";

function AuthGate({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<"checking" | "ok" | "fail">("checking");
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    api
      .get("/auth/me")
      .then(() => {
        if (!cancelled) setStatus("ok");
      })
      .catch(() => {
        if (!cancelled) setStatus("fail");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "checking") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          color: "var(--text-secondary)",
          fontSize: 14,
        }}
      >
        Loading…
      </div>
    );
  }
  if (status === "fail") {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }
  return <>{children}</>;
}

function LogoutButton() {
  const navigate = useNavigate();
  const onClick = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    navigate("/login", { replace: true });
  };
  return (
    <button
      onClick={onClick}
      style={{
        background: "transparent",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "rgba(255,255,255,0.75)",
        padding: "8px 12px",
        borderRadius: 8,
        fontSize: 13,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        justifyContent: "center",
      }}
    >
      <LogOut size={14} /> Sign out
    </button>
  );
}

function AdminLayout() {
  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <h1>MOT</h1>
          <div className="brand-sub">Student Management</div>
        </div>
        <nav>
          <NavLink to="/" end>
            <GraduationCap size={18} /> Classes
          </NavLink>
          <NavLink to="/students">
            <Users size={18} /> Students
          </NavLink>
          <NavLink to="/follow-ups">
            <AlertCircle size={18} /> Follow-ups
          </NavLink>
        </nav>
        <div className="sidebar-footer" style={{ padding: 16 }}>
          <LogoutButton />
        </div>
      </aside>
      <main className="main">
        <Routes>
          <Route path="/" element={<Classes />} />
          <Route path="/classes/:id" element={<ClassDetail />} />
          <Route path="/students" element={<Students />} />
          <Route path="/students/:id" element={<StudentDetail />} />
          <Route path="/follow-ups" element={<FollowUps />} />
        </Routes>
      </main>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();

  // Public homework page — no sidebar, no auth
  if (location.pathname.startsWith("/hw/")) {
    return (
      <Routes>
        <Route path="/hw/:classId" element={<Homework />} />
      </Routes>
    );
  }

  // Login page — no sidebar, no auth
  if (location.pathname === "/login") {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
      </Routes>
    );
  }

  // Everything else: admin-gated
  return (
    <AuthGate>
      <AdminLayout />
    </AuthGate>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
